import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { Loader2, ShieldAlert, ChevronsUpDown, Check, Search } from "lucide-react";
import { setTaxOverride, getTaxNumbers } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";

export const TAX_OVERRIDE_ROLES = ["project_manager", "account_manager", "service_manager", "admin"];
export const MAX_TAX_OVERRIDE_RATE = 15;

export function useCanOverrideTax() {
  const { user } = useAuth();
  return TAX_OVERRIDE_ROLES.includes(String(user?.role || "").toLowerCase());
}

const money = (n) => `$${(Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Shared form state for the dialog + inline variants. Resets every time
// `open` flips true; loads the Tax Decision Tree tax numbers.
function useTaxOverrideForm({ open, docType, docId, woId, current, onApplied, onOpenChange }) {
  const existing = current?.tax_override || null;
  const [type, setType] = useState(existing?.type || "rate");
  const [rate, setRate] = useState("");
  const [amount, setAmount] = useState("");
  const [taxNo, setTaxNo] = useState("");
  const [taxNos, setTaxNos] = useState([]);
  const [otherNo, setOtherNo] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setType(existing?.type || "rate");
    setRate(existing?.type === "rate" ? String(existing.rate) : String(current?.tax_rate ?? ""));
    setAmount(existing?.type === "amount" ? String(existing.amount) : (current?.tax_amount != null ? (Number(current.tax_amount) || 0).toFixed(2) : ""));
    const initial = String(existing?.tax_no || current?.tax_authority || current?.tax_no || "").toUpperCase();
    setTaxNo(initial);
    getTaxNumbers()
      .then(r => {
        const list = r.data || [];
        setTaxNos(list);
        setOtherNo(!!initial && !list.some(t => t.tax_no === initial));
      })
      .catch(() => { setTaxNos([]); setOtherNo(true); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const base = Number(current?.taxable_base) || 0;
  const rateNum = parseFloat(rate);
  const amountNum = parseFloat(amount);
  const rateBad = type === "rate" && (!Number.isFinite(rateNum) || rateNum < 0 || rateNum > MAX_TAX_OVERRIDE_RATE);
  const amountBad = type === "amount" && (!Number.isFinite(amountNum) || amountNum < 0);
  const taxNoBad = !taxNo.trim();
  const resulting = type === "rate" ? (Number.isFinite(rateNum) ? base * rateNum / 100 : 0) : (Number.isFinite(amountNum) ? amountNum : 0);
  const disabled = rateBad || amountBad || taxNoBad || saving;

  const apply = async () => {
    setSaving(true);
    try {
      const body = { type, tax_no: taxNo.trim(), wo_id: woId || undefined };
      if (type === "rate") body.rate = rateNum; else body.amount = Math.round(amountNum * 100) / 100;
      const res = await setTaxOverride(docType, docId, body);
      toast.success(`Tax manually set — ${type === "rate" ? `${rateNum}%` : money(amountNum)} (tax no ${taxNo.trim()})`);
      onApplied?.(res.data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not apply the tax override");
    } finally {
      setSaving(false);
    }
  };

  return { existing, type, setType, rate, setRate, amount, setAmount, taxNo, setTaxNo, taxNos, otherNo, setOtherNo,
           base, rateNum, amountNum, rateBad, amountBad, resulting, disabled, saving, apply };
}

function TaxNoPicker({ f, compact = false }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const selected = f.taxNos.find(t => t.tax_no === f.taxNo);
  const needle = q.trim().toLowerCase();
  const shown = needle ? f.taxNos.filter(t => t.tax_no.toLowerCase().includes(needle) || String(t.label || "").toLowerCase().includes(needle)) : f.taxNos;
  const pick = (t) => {
    f.setOtherNo(false); f.setTaxNo(t.tax_no);
    // Exempt / out-of-state post at 0% — set the rate so one pick does the whole job.
    if (t.zero_rated) { f.setType("rate"); f.setRate("0"); }
    setOpen(false); setQ("");
  };
  return (
    <>
      <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQ(""); }}>
        <PopoverTrigger asChild>
          <button type="button" role="combobox" aria-expanded={open} data-testid="tax-override-tax-no-select"
            className={`${compact ? "h-8 w-52 text-xs" : "h-9 w-full text-sm"} flex items-center justify-between gap-2 rounded-md border border-input bg-background px-3 text-left shadow-sm hover:bg-slate-50`}>
            {f.otherNo ? <span className="text-slate-500">Other — typed manually</span>
              : f.taxNo ? <span className="truncate"><span className="font-mono font-semibold">{f.taxNo}</span>{selected?.label && <span className="ml-2 text-xs text-slate-500">{selected.label}</span>}</span>
              : <span className="text-slate-400">Tax no</span>}
            <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 opacity-50" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-80 p-1" onKeyDown={(e) => e.stopPropagation()}>
          <div className="relative mb-1">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} autoFocus placeholder="Type a tax no or city — e.g. DEN, Denver, exempt"
              className="h-8 w-full rounded border border-slate-200 bg-transparent pl-7 pr-2 text-xs outline-none focus:border-brand" data-testid="tax-override-tax-no-search" />
          </div>
          <div className="max-h-64 overflow-y-auto" data-testid="tax-override-tax-no-list">
            {shown.length === 0 && <div className="px-2 py-1.5 text-xs text-slate-400">No tax no matches "{q}"</div>}
            {shown.map(t => (
              <button key={t.tax_no} type="button" onClick={() => pick(t)} data-testid={`tax-override-tax-no-option-${t.tax_no}`}
                className={`flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 text-left text-xs hover:bg-slate-100 ${t.tax_no === f.taxNo && !f.otherNo ? "bg-slate-50" : ""}`}>
                <span className="truncate"><span className="font-mono font-semibold">{t.tax_no}</span><span className="ml-2 text-slate-500">{t.label}</span>{t.zero_rated && <span className="ml-2 rounded bg-emerald-50 px-1 text-[10px] text-emerald-700">0%</span>}</span>
                {t.tax_no === f.taxNo && !f.otherNo && <Check className="h-3 w-3 shrink-0 text-brand" />}
              </button>
            ))}
            <button type="button" onClick={() => { f.setOtherNo(true); f.setTaxNo(""); setOpen(false); setQ(""); }} data-testid="tax-override-tax-no-option-other"
              className="mt-1 flex w-full items-center rounded border-t border-slate-100 px-2 py-1.5 text-left text-xs text-slate-600 hover:bg-slate-100">Other — type manually…</button>
          </div>
        </PopoverContent>
      </Popover>
      {f.otherNo && (
        <Input value={f.taxNo} onChange={(e) => f.setTaxNo(e.target.value.toUpperCase())} maxLength={20} placeholder="e.g. DEN"
               className={`${compact ? "h-8 w-28 text-xs" : "h-9 w-40"} font-mono`} autoFocus data-testid="tax-override-tax-no-input" />
      )}
    </>
  );
}

// Inline variant (iter331) — lives in the totals block; unlocked by the
// "Override tax" button, applied in one click (still logged server-side).
export function TaxOverrideInline(props) {
  const { open, onOpenChange } = props;
  const f = useTaxOverrideForm(props);
  if (!open) return null;
  return (
    <div className="rounded-md border border-amber-300 bg-amber-50/70 px-3 py-2 space-y-2 text-xs" data-testid="tax-override-inline"
         onKeyDown={(e) => { if (e.key === "Escape") { e.stopPropagation(); onOpenChange(false); } }}>
      <div className="flex items-center gap-2 text-amber-900 font-semibold">
        <ShieldAlert className="w-3.5 h-3.5" /> {f.existing ? "Change manual tax" : "Override tax"}
        <span className="ml-auto font-normal text-amber-800">Calculated {Number(props.current?.tax_rate) || 0}% on {money(f.base)} = {money(f.base * (Number(props.current?.tax_rate) || 0) / 100)}</span>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex rounded-md border border-amber-300 overflow-hidden" role="radiogroup">
          {[["rate", "Rate %"], ["amount", "Exact $"]].map(([v, label]) => (
            <button key={v} type="button" onClick={() => f.setType(v)} data-testid={`tax-override-type-${v}`}
              className={`px-2 py-1 ${f.type === v ? "bg-amber-600 text-white font-semibold" : "bg-white text-slate-600 hover:bg-amber-100"}`}>
              {label}
            </button>
          ))}
        </div>
        {f.type === "rate" ? (
          <div className="flex items-center gap-1">
            <Input type="number" step="0.001" min="0" max={MAX_TAX_OVERRIDE_RATE} value={f.rate} onChange={(e) => f.setRate(e.target.value)}
                   className="h-8 w-20 text-xs text-right" data-testid="tax-override-rate-input" />
            <span>%</span>
          </div>
        ) : (
          <div className="flex items-center gap-1">
            <span>$</span>
            <Input type="number" step="0.01" min="0" value={f.amount} onChange={(e) => f.setAmount(e.target.value)}
                   className="h-8 w-24 text-xs text-right" data-testid="tax-override-amount-input" />
          </div>
        )}
        <TaxNoPicker f={f} compact />
        <span className="tabular-nums font-semibold text-slate-700" data-testid="tax-override-preview">= {money(f.resulting)}</span>
        <div className="ml-auto flex items-center gap-1">
          <Button type="button" variant="ghost" size="sm" className="h-8 text-xs" onClick={() => onOpenChange(false)} data-testid="tax-override-cancel-btn">Cancel</Button>
          <Button type="button" size="sm" onClick={f.apply} disabled={f.disabled} className="h-8 text-xs bg-amber-600 hover:bg-amber-700 text-white" data-testid="tax-override-confirm-btn">
            {f.saving && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />} Apply
          </Button>
        </div>
      </div>
      {f.rateBad && f.rate !== "" && <p className="text-red-600" data-testid="tax-override-rate-error">Enter a rate between 0% and {MAX_TAX_OVERRIDE_RATE}%.</p>}
      {!f.taxNo.trim() && <p className="text-amber-800">Pick the tax no this document posts to.</p>}
    </div>
  );
}

// One dialog for proposals, change orders, invoices and work orders.
// Rate (%) or exact dollars; the Tax No must be confirmed on every apply.
export function TaxOverrideDialog(props) {
  const { open, onOpenChange, current } = props;
  const f = useTaxOverrideForm(props);
  const [confirming, setConfirming] = useState(false);
  useEffect(() => { if (open) setConfirming(false); }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="tax-override-dialog" onKeyDown={(e) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ShieldAlert className="w-4 h-4 text-amber-600" /> {f.existing ? "Change manual tax" : "Override tax"}</DialogTitle>
          <DialogDescription>
            Calculated: {Number(current?.tax_rate) || 0}% on {money(f.base)} = {money(f.base * (Number(current?.tax_rate) || 0) / 100)}. The customer sees rate and amount only.
          </DialogDescription>
        </DialogHeader>

        {!confirming ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-2" role="radiogroup">
              {[["rate", "Rate override (%)"], ["amount", "Exact dollar amount"]].map(([v, label]) => (
                <button key={v} type="button" onClick={() => f.setType(v)} data-testid={`tax-override-type-${v}`}
                  className={`text-xs px-3 py-2 rounded-md border transition-colors ${f.type === v ? "border-amber-500 bg-amber-50 text-amber-900 font-semibold" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                  {label}
                </button>
              ))}
            </div>
            {f.type === "rate" ? (
              <div className="space-y-1">
                <Label className="text-xs">Tax rate (0 – {MAX_TAX_OVERRIDE_RATE}%)</Label>
                <div className="flex items-center gap-2">
                  <Input type="number" step="0.001" min="0" max={MAX_TAX_OVERRIDE_RATE} value={f.rate} onChange={(e) => f.setRate(e.target.value)} className="h-9 w-32" data-testid="tax-override-rate-input" />
                  <span className="text-sm text-slate-500">%</span>
                </div>
                {f.rateBad && f.rate !== "" && <p className="text-xs text-red-600" data-testid="tax-override-rate-error">Enter a rate between 0% and {MAX_TAX_OVERRIDE_RATE}%.</p>}
                <p className="text-xs text-slate-500">Sticks through line-item edits — dollars recompute at this rate.</p>
              </div>
            ) : (
              <div className="space-y-1">
                <Label className="text-xs">Tax Amount</Label>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-500">$</span>
                  <Input type="number" step="0.01" min="0" value={f.amount} onChange={(e) => f.setAmount(e.target.value)} className="h-9 w-36" data-testid="tax-override-amount-input" />
                </div>
                <p className="text-xs text-slate-500">Sticks as-is; flagged for review if line items change afterwards.</p>
              </div>
            )}
            <div className="space-y-1">
              <Label className="text-xs">Tax No <span className="text-red-500">*</span></Label>
              <TaxNoPicker f={f} />
              <p className="text-xs text-slate-500">Confirm the tax authority this document posts to.</p>
            </div>
            <div className="rounded-md bg-slate-50 border px-3 py-2 text-sm flex justify-between" data-testid="tax-override-preview">
              <span className="text-slate-500">Resulting tax</span>
              <span className="tabular-nums font-semibold">{money(f.resulting)}</span>
            </div>
          </div>
        ) : (
          <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-3 text-sm space-y-1" data-testid="tax-override-confirm-box">
            <p className="font-semibold text-amber-900">Confirm manual tax</p>
            <p className="text-amber-900">Set tax to <strong>{f.type === "rate" ? `${f.rateNum}%` : money(f.amountNum)}</strong> ({money(f.resulting)}) under tax no <strong className="font-mono">{f.taxNo.trim()}</strong>?</p>
            <p className="text-xs text-amber-800">This is logged with your name, role and the prior calculated tax.</p>
          </div>
        )}

        <DialogFooter className="gap-2">
          {confirming ? (
            <>
              <Button type="button" variant="ghost" onClick={() => setConfirming(false)} disabled={f.saving} data-testid="tax-override-back-btn">Back</Button>
              <Button type="button" onClick={f.apply} disabled={f.saving} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="tax-override-confirm-btn">
                {f.saving && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />} Apply override
              </Button>
            </>
          ) : (
            <>
              <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} data-testid="tax-override-cancel-btn">Cancel</Button>
              <Button type="button" onClick={() => setConfirming(true)} disabled={f.disabled} data-testid="tax-override-continue-btn">Continue</Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
